/**
 * Date-based grouping utilities for Upcoming, Logbook, and Trash views.
 *
 * Two grouping strategies:
 * 1. **Upcoming** — 7 individual days (tomorrow + 6), then months to end of
 *    next year, then years based on latest startAt in the dataset.
 *    ALL periods are shown even when empty (future DnD drop targets).
 *
 * 2. **History** (Logbook/Trash) — OmniFocus-style progressive model:
 *    today → yesterday → weekdays → last week → this month → last month →
 *    monthly → yearly → "Äldre". Only non-empty groups are shown.
 *
 * @see docs/plans/view-and-dnd-master-plan.md §6 and §12
 */

import dayjs from "dayjs"
import type { Dayjs } from "dayjs"
import type { ViewItem } from "../hooks/useViewData"
import { parseDbDate } from "@totodo/operations"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DatePeriod {
  /** Unique key for this period, e.g. "2025-02-19", "2025-03", "2028" */
  key: string
  /** Display label, e.g. "Thursday", "March 5 - 31", "April", "2028" */
  label: string
  /** For day periods: the date number (e.g. "26"). Undefined for month/year. */
  dateNumber?: string
  /** Inclusive start timestamp (ms) */
  startMs: number
  /** Inclusive end timestamp (ms) */
  endMs: number
  /** Period granularity */
  type: "day" | "month" | "year"
}

export interface HistoryPeriod {
  /** Unique key, e.g. "today", "yesterday", "monday", "last-week", "2025-03" */
  key: string
  /** Display label */
  label: string
  /** Inclusive start timestamp (ms) */
  startMs: number
  /** Inclusive end timestamp (ms) */
  endMs: number
}

export interface DateGroup<T = ViewItem> {
  period: DatePeriod | HistoryPeriod
  items: T[]
}

// ---------------------------------------------------------------------------
// Upcoming period generation
// ---------------------------------------------------------------------------

/**
 * Generate all visible periods for the Upcoming view.
 *
 * 1. 7 individual days (tomorrow + 6 following days)
 * 2. Monthly periods from the end of those 7 days through end of next year
 * 3. Yearly periods for 2+ years ahead (only if data exists that far)
 *
 * All periods are generated regardless of whether tasks exist in them —
 * empty periods serve as DnD drop targets (spec §6).
 *
 * @param latestStartAtMs - The latest startAt timestamp in the dataset (ms).
 *   Used to determine how many year-periods to generate. Pass 0 or undefined
 *   if the dataset is empty.
 */
export function generateUpcomingPeriods(
  latestStartAtMs?: number,
): DatePeriod[] {
  const periods: DatePeriod[] = []
  const tomorrow = dayjs().add(1, "day").startOf("day")

  // ── 7 individual days ──────────────────────────────────────────────────
  for (let i = 0; i < 7; i++) {
    const d = tomorrow.add(i, "day")
    periods.push({
      key: d.format("YYYY-MM-DD"),
      label: capitalize(d.format("dddd")),
      dateNumber: d.format("D"),
      startMs: d.startOf("day").valueOf(),
      endMs: d.endOf("day").valueOf(),
      type: "day",
    })
  }

  // ── Monthly periods ────────────────────────────────────────────────────
  // Start from the day after the last individual day
  const dailyEnd = tomorrow.add(6, "day") // last individual day
  let monthCursor = dailyEnd.add(1, "day").startOf("month")
  // If dailyEnd is mid-month, the first "month" period starts from the day
  // after dailyEnd, not from the 1st — but the key is still the month key.
  const firstMonthStart = dailyEnd.add(1, "day")
  const endOfNextYear = dayjs().add(1, "year").endOf("year")

  // Handle partial first month
  if (firstMonthStart.month() === dailyEnd.month()) {
    // The daily period ends mid-month — create a partial month period
    const monthEnd = firstMonthStart.endOf("month")
    periods.push({
      key: firstMonthStart.format("YYYY-MM"),
      label: formatPartialMonthLabel(firstMonthStart, monthEnd),
      startMs: firstMonthStart.startOf("day").valueOf(),
      endMs: monthEnd.valueOf(),
      type: "month",
    })
    monthCursor = monthEnd.add(1, "day").startOf("month")
  }

  // Full months
  while (
    monthCursor.valueOf() <= endOfNextYear.valueOf() &&
    monthCursor.startOf("month").valueOf() <= endOfNextYear.valueOf()
  ) {
    const monthEnd = monthCursor.endOf("month")
    periods.push({
      key: monthCursor.format("YYYY-MM"),
      label: formatFullMonthLabel(monthCursor),
      startMs: monthCursor.startOf("day").valueOf(),
      endMs: monthEnd.valueOf(),
      type: "month",
    })
    monthCursor = monthEnd.add(1, "day").startOf("month")
  }

  // ── Yearly periods (2+ years ahead) ───────────────────────────────────
  if (latestStartAtMs && latestStartAtMs > endOfNextYear.valueOf()) {
    let yearCursor = endOfNextYear.add(1, "day").startOf("year")
    const latestYear = dayjs(latestStartAtMs).endOf("year")
    while (yearCursor.valueOf() <= latestYear.valueOf()) {
      periods.push({
        key: yearCursor.format("YYYY"),
        label: yearCursor.format("YYYY"),
        startMs: yearCursor.startOf("year").valueOf(),
        endMs: yearCursor.endOf("year").valueOf(),
        type: "year",
      })
      yearCursor = yearCursor.add(1, "year")
    }
  }

  return periods
}

/**
 * Group Upcoming view items into date periods.
 *
 * Items are assigned to periods based on `startAtData.startAt`.
 * Items that don't match any period are silently dropped (shouldn't happen
 * if queryUpcoming + period generation use the same date range).
 *
 * @returns DateGroup[] — one per period, preserving period order. Empty
 *          groups are included (empty items array).
 */
export function groupUpcomingItems(
  items: ViewItem[],
  periods: DatePeriod[],
): DateGroup<ViewItem>[] {
  // Build a map from period key → items
  const groupMap = new Map<string, ViewItem[]>()
  for (const p of periods) {
    groupMap.set(p.key, [])
  }

  for (const item of items) {
    const ts = parseDbDate(item.startAtData?.startAt) ?? 0
    if (!ts) continue

    // Find the matching period (binary-search-friendly since periods are sorted)
    const period = findPeriod(ts, periods)
    if (period) {
      groupMap.get(period.key)!.push(item)
    }
  }

  return periods.map((p) => ({
    period: p,
    items: groupMap.get(p.key) ?? [],
  }))
}

// ---------------------------------------------------------------------------
// History period generation (Logbook / Trash)
// ---------------------------------------------------------------------------

/**
 * Generate history periods and group items into them.
 *
 * Uses OmniFocus-style progressive model (spec §12):
 * - Today
 * - Yesterday
 * - Individual weekdays (this week, before yesterday)
 * - Last week
 * - This month (after last week)
 * - Last month
 * - Monthly (same year, older than last month)
 * - Yearly (previous years)
 * - Äldre (catch-all)
 *
 * Only non-empty groups are returned (unlike Upcoming which shows all).
 *
 * @param items - ViewItems sorted by timestamp DESC (most recent first)
 * @param getTimestamp - Extracts the grouping timestamp from a ViewItem.
 *   Logbook uses `statusAt`, Trash uses `deletedAt`.
 */
export function groupHistoryItems(
  items: ViewItem[],
  getTimestamp: (item: ViewItem) => number,
): DateGroup<ViewItem>[] {
  const now = dayjs()
  const todayStart = now.startOf("day")
  const todayEnd = now.endOf("day")
  const yesterdayStart = todayStart.subtract(1, "day")
  const yesterdayEnd = yesterdayStart.endOf("day")

  // Build all possible history periods
  const periods = buildHistoryPeriods(now)

  // Group items
  const groupMap = new Map<string, ViewItem[]>()
  for (const p of periods) {
    groupMap.set(p.key, [])
  }

  for (const item of items) {
    const ts = getTimestamp(item)
    if (ts <= 0) {
      // Items without a valid timestamp go directly to "older"
      const older = groupMap.get("older")
      if (older) older.push(item)
      continue
    }

    const key = getHistoryGroupKey(
      ts,
      now,
      todayStart,
      todayEnd,
      yesterdayStart,
      yesterdayEnd,
    )
    if (groupMap.has(key)) {
      groupMap.get(key)!.push(item)
    } else {
      // Fallback: items outside all defined periods go to "older"
      const older = groupMap.get("older")
      if (older) older.push(item)
    }
  }

  // Return only non-empty groups, preserving period order
  return periods
    .filter((p) => (groupMap.get(p.key)?.length ?? 0) > 0)
    .map((p) => ({
      period: p,
      items: groupMap.get(p.key) ?? [],
    }))
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Format a partial month label: "March 5 - 31" (v1 style).
 * Shows the range from start day to end of month.
 */
function formatPartialMonthLabel(start: Dayjs, end: Dayjs): string {
  const month = capitalize(start.format("MMMM"))
  return `${month} ${start.format("D")} - ${end.format("D")}`
}

/**
 * Format a full month label: "April" (v1 style).
 * No year when it's within the current or next year context.
 */
function formatFullMonthLabel(d: Dayjs): string {
  return capitalize(d.format("MMMM"))
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** Find which period a timestamp belongs to (linear scan — periods are few). */
function findPeriod(
  timestampMs: number,
  periods: DatePeriod[],
): DatePeriod | undefined {
  for (const p of periods) {
    if (timestampMs >= p.startMs && timestampMs <= p.endMs) {
      return p
    }
  }
  return undefined
}

/**
 * Build history periods for the OmniFocus progressive model.
 * Returns periods in order from most recent to oldest.
 */
function buildHistoryPeriods(now: Dayjs): HistoryPeriod[] {
  const periods: HistoryPeriod[] = []
  const todayStart = now.startOf("day")

  // Today
  periods.push({
    key: "today",
    label: "Idag",
    startMs: todayStart.valueOf(),
    endMs: todayStart.endOf("day").valueOf(),
  })

  // Yesterday
  const yesterdayStart = todayStart.subtract(1, "day")
  periods.push({
    key: "yesterday",
    label: "Igår",
    startMs: yesterdayStart.valueOf(),
    endMs: yesterdayStart.endOf("day").valueOf(),
  })

  // Individual weekdays this week (before yesterday, back to start of week)
  // Walk backwards from day-before-yesterday to start of week
  const weekStart = now.startOf("week") // Monday (with locale)
  let cursor = yesterdayStart.subtract(1, "day")
  while (cursor.valueOf() >= weekStart.valueOf()) {
    const dayKey = cursor.format("YYYY-MM-DD")
    periods.push({
      key: dayKey,
      label: capitalize(cursor.format("dddd")), // "Måndag", "Tisdag", etc.
      startMs: cursor.startOf("day").valueOf(),
      endMs: cursor.endOf("day").valueOf(),
    })
    cursor = cursor.subtract(1, "day")
  }

  // Last week
  const lastWeekStart = weekStart.subtract(1, "week")
  const lastWeekEnd = weekStart.subtract(1, "day").endOf("day")
  periods.push({
    key: "last-week",
    label: "Förra veckan",
    startMs: lastWeekStart.valueOf(),
    endMs: lastWeekEnd.valueOf(),
  })

  // This month (after last week, back to start of month)
  const monthStart = now.startOf("month")
  if (lastWeekStart.valueOf() > monthStart.valueOf()) {
    // There's a gap between month start and last week start
    periods.push({
      key: "this-month",
      label: "Denna månad",
      startMs: monthStart.valueOf(),
      endMs: lastWeekStart.subtract(1, "millisecond").valueOf(),
    })
  }

  // Last month
  const lastMonthStart = monthStart.subtract(1, "month")
  const lastMonthEnd = monthStart.subtract(1, "day").endOf("day")
  periods.push({
    key: "last-month",
    label: "Förra månaden",
    startMs: lastMonthStart.valueOf(),
    endMs: lastMonthEnd.valueOf(),
  })

  // Monthly periods for the rest of this year (before last month)
  let monthCursor = lastMonthStart.subtract(1, "month")
  const yearStart = now.startOf("year")
  while (monthCursor.valueOf() >= yearStart.valueOf()) {
    const monthEnd = monthCursor.endOf("month")
    periods.push({
      key: monthCursor.format("YYYY-MM"),
      label: capitalize(monthCursor.format("MMMM YYYY")),
      startMs: monthCursor.startOf("month").valueOf(),
      endMs: monthEnd.valueOf(),
    })
    monthCursor = monthCursor.subtract(1, "month")
  }

  // Yearly periods for previous years (most recent first)
  // Generate up to 10 years back as a reasonable limit
  let yearCursor = yearStart.subtract(1, "year")
  for (let i = 0; i < 10; i++) {
    periods.push({
      key: yearCursor.format("YYYY"),
      label: yearCursor.format("YYYY"),
      startMs: yearCursor.startOf("year").valueOf(),
      endMs: yearCursor.endOf("year").valueOf(),
    })
    yearCursor = yearCursor.subtract(1, "year")
  }

  // Catch-all: "Äldre" (anything before the yearly periods)
  periods.push({
    key: "older",
    label: "Äldre",
    startMs: 0,
    endMs: yearCursor.endOf("year").valueOf(),
  })

  return periods
}

/**
 * Determine which history group key a timestamp belongs to.
 */
function getHistoryGroupKey(
  timestampMs: number,
  now: Dayjs,
  todayStart: Dayjs,
  todayEnd: Dayjs,
  yesterdayStart: Dayjs,
  yesterdayEnd: Dayjs,
): string {
  const date = dayjs(timestampMs)

  // Today
  if (
    timestampMs >= todayStart.valueOf() &&
    timestampMs <= todayEnd.valueOf()
  ) {
    return "today"
  }

  // Yesterday
  if (
    timestampMs >= yesterdayStart.valueOf() &&
    timestampMs <= yesterdayEnd.valueOf()
  ) {
    return "yesterday"
  }

  // This week (before yesterday)
  const weekStart = now.startOf("week")
  if (
    timestampMs >= weekStart.valueOf() &&
    timestampMs < yesterdayStart.valueOf()
  ) {
    return date.format("YYYY-MM-DD")
  }

  // Last week
  const lastWeekStart = weekStart.subtract(1, "week")
  if (
    timestampMs >= lastWeekStart.valueOf() &&
    timestampMs < weekStart.valueOf()
  ) {
    return "last-week"
  }

  // This month (before last week)
  const monthStart = now.startOf("month")
  if (
    timestampMs >= monthStart.valueOf() &&
    timestampMs < lastWeekStart.valueOf()
  ) {
    return "this-month"
  }

  // Last month
  const lastMonthStart = monthStart.subtract(1, "month")
  if (
    timestampMs >= lastMonthStart.valueOf() &&
    timestampMs < monthStart.valueOf()
  ) {
    return "last-month"
  }

  // Same year, older months
  const yearStart = now.startOf("year")
  if (timestampMs >= yearStart.valueOf()) {
    return date.format("YYYY-MM")
  }

  // Previous years
  if (timestampMs > 0) {
    return date.format("YYYY")
  }

  return "older"
}

// ---------------------------------------------------------------------------
// Timestamp extractors for Logbook and Trash
// ---------------------------------------------------------------------------

/** Extract grouping timestamp for Logbook (statusAt — when task was completed) */
export function logbookTimestamp(item: ViewItem): number {
  return parseDbDate(item.task.statusAt) ?? 0
}

/**
 * Extract grouping timestamp for Trash.
 * Prefers deletedAt, falls back to updatedAt (older items may lack deletedAt).
 */
export function trashTimestamp(item: ViewItem): number {
  const deletedAt = Number(item.task.deletedAt ?? "0")
  if (deletedAt > 0) return deletedAt
  // Fallback: updatedAt is set by deleteTask/moveToTrash
  return Number(item.task.updatedAt ?? "0")
}
